import React, { useState } from 'react';
import { Movements } from './pages/Movements';
import { Stock } from './pages/Stock';
import { Reports } from './pages/Reports';
import { Separation } from './pages/Separation';
import { IconBox, IconHistory, IconQrCode, IconClipboard } from './components/Icons';
import { usePreferences } from './contexts/PreferencesContext';
import { useI18n } from './i18n';

type View = 'MOVEMENTS' | 'STOCK' | 'REPORTS' | 'SEPARATION';

function App() {
  const [currentView, setCurrentView] = useState<View>('MOVEMENTS');
  const { theme, language, setLanguage, setTheme } = usePreferences();
  const { t } = useI18n();

  return (
    // Use h-screen and overflow-hidden to lock the body scroll
    <div className={`h-screen w-full font-sans flex flex-col md:flex-row overflow-hidden theme-${theme} bg-[var(--color-background)] text-[var(--color-text)]`}>

      {/* Sidebar for Desktop */}
      <aside className="hidden md:flex flex-col w-64 bg-surface border-r border-subtle h-full">
        <div className="p-6 border-b border-subtle flex-shrink-0 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-xl font-bold text-accent">
              {t('app.name')}
            </h1>
            <div className="text-[11px] text-muted font-mono">{t('app.version')}</div>
          </div>
          <div className="space-y-2">
            <label className="text-[11px] uppercase font-semibold text-muted">
              {t('preferences.theme')}
              <select
                className="mt-1 w-full bg-surface-strong border border-subtle rounded-md px-2 py-1 text-sm text-primary"
                value={theme}
                onChange={(e) => setTheme(e.target.value as any)}
              >
                <option value="light">{t('preferences.theme.light')}</option>
                <option value="dark">{t('preferences.theme.dark')}</option>
                <option value="high-contrast">{t('preferences.theme.high-contrast')}</option>
              </select>
            </label>
            <label className="text-[11px] uppercase font-semibold text-muted">
              {t('preferences.language')}
              <select
                className="mt-1 w-full bg-surface-strong border border-subtle rounded-md px-2 py-1 text-sm text-primary"
                value={language}
                onChange={(e) => setLanguage(e.target.value as any)}
              >
                <option value="pt-BR">{t('preferences.language.pt-BR')}</option>
                <option value="en">{t('preferences.language.en')}</option>
              </select>
            </label>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <NavButton
            active={currentView === 'MOVEMENTS'}
            onClick={() => setCurrentView('MOVEMENTS')}
            icon={<IconQrCode />}
            label={t('nav.scanner')}
          />
          <NavButton
            active={currentView === 'SEPARATION'}
            onClick={() => setCurrentView('SEPARATION')}
            icon={<IconClipboard />}
            label={t('nav.picking')}
          />
          <NavButton
            active={currentView === 'STOCK'}
            onClick={() => setCurrentView('STOCK')}
            icon={<IconBox />}
            label={t('nav.inventory')}
          />
          <NavButton
            active={currentView === 'REPORTS'}
            onClick={() => setCurrentView('REPORTS')}
            icon={<IconHistory />}
            label={t('nav.history')}
          />
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-full relative bg-[var(--color-background)]">
        {/* Header (Mobile Only) */}
        <header className="md:hidden flex items-center justify-between p-4 bg-surface z-10 flex-shrink-0 border-b border-subtle">
           <div>
             <h1 className="text-xl font-bold text-accent">{t('app.name')}</h1>
             <div className="text-[11px] text-muted font-mono">{t('app.version')}</div>
           </div>
           <div className="flex items-center gap-2">
             <select
               className="bg-surface-strong border border-subtle rounded-md px-2 py-1 text-sm"
               value={language}
               onChange={(e) => setLanguage(e.target.value as any)}
               aria-label={t('preferences.language')}
             >
               <option value="pt-BR">PT</option>
               <option value="en">EN</option>
             </select>
             <select
               className="bg-surface-strong border border-subtle rounded-md px-2 py-1 text-sm"
               value={theme}
               onChange={(e) => setTheme(e.target.value as any)}
               aria-label={t('preferences.theme')}
             >
               <option value="light">☀️</option>
               <option value="dark">🌙</option>
               <option value="high-contrast">⚡</option>
             </select>
           </div>
        </header>

        {/* Scrollable Content */}
        {/* pb-24 ensures content isn't hidden behind the fixed mobile nav */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8 max-w-3xl mx-auto md:max-w-full w-full pb-24 md:pb-8">
           {currentView === 'MOVEMENTS' && <Movements />}
           {currentView === 'SEPARATION' && <Separation />}
           {currentView === 'STOCK' && <Stock />}
           {currentView === 'REPORTS' && <Reports />}
        </main>

        {/* Mobile Bottom Navigation (Fixed within the flex container logic) */}
        <div className="md:hidden absolute bottom-0 left-0 right-0 bg-surface border-t border-subtle flex justify-around p-2 pb-safe shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.25)] z-50">
          <MobileNavButton
            active={currentView === 'MOVEMENTS'}
            onClick={() => setCurrentView('MOVEMENTS')}
            icon={<IconQrCode />}
            label={t('nav.scan')}
          />
          <MobileNavButton
            active={currentView === 'SEPARATION'}
            onClick={() => setCurrentView('SEPARATION')}
            icon={<IconClipboard />}
            label={t('nav.picking')}
          />
          <MobileNavButton
            active={currentView === 'STOCK'}
            onClick={() => setCurrentView('STOCK')}
            icon={<IconBox />}
            label={t('nav.stock')}
          />
          <MobileNavButton
            active={currentView === 'REPORTS'}
            onClick={() => setCurrentView('REPORTS')}
            icon={<IconHistory />}
            label={t('nav.history')}
          />
        </div>
      </div>
    </div>
  );
}

const NavButton = ({ active, onClick, icon, label }: any) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors border border-transparent ${
      active
        ? 'bg-accent-soft text-accent border-accent'
        : 'text-muted hover:bg-surface-strong hover:text-primary'
    }`}
  >
    {React.cloneElement(icon, { className: "w-5 h-5" })}
    {label}
  </button>
);

const MobileNavButton = ({ active, onClick, icon, label }: any) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center justify-center p-2 rounded-lg w-full transition-colors active:scale-95 ${
      active
        ? 'text-accent'
        : 'text-muted'
    }`}
  >
    {React.cloneElement(icon, { className: "w-6 h-6 mb-1" })}
    <span className="text-[10px] font-medium">{label}</span>
  </button>
);

export default App;
